import { Handle, Position } from "@xyflow/react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { MdClose } from "react-icons/md";
import { PopoverArrow } from "@radix-ui/react-popover";
import { cn } from "../../../lib/utils";

import { BlocksType, NodeData } from "./types";
import colors from "./colors";
import { usePortName } from "./usePortName";

interface HandleProps {
  data: NodeData;
  edges: any[];
  nodes: any[];
  id: string;
  globalinputs?: Record<string, string>;
  isConnectDemo?: boolean;
  isMobile?: boolean;
  isConnectable?: boolean;
}
const Handles = ({
  data,
  edges,
  id,
  isConnectDemo,
  isMobile,
  isConnectable,
}: HandleProps) => {
  return (
    <div className="flex flex-col w-full pointer-events-none">
      <div className="flex flex-row justify-between w-full">
        {/* Left column: Input Ports */}
        <div className="flex flex-col items-start min-w-0">
          {data?.inputs?.map((port: any, index: number) => {
            const ids = port?.id;

            const { name, singleEdge, connectedNodeType, connectedNodeName } =
              usePortName({
                port,
                edges,
                nodeId: id,
                direct: true,
              });

            return (
              <div
                className="custom__port relative flex items-center h-10 px-0 pointer-events-auto"
                key={`input-${index}`}
              >
                <Handle
                  type="target"
                  position={Position.Left}
                  className={cn(
                    "port-canvas nodrag",
                    isConnectDemo ? "!bg-[#316ACD] animate-pulse" : "",
                  )}
                  id={ids}
                  style={{
                    top: "50%",
                    transform: "translateY(-50%)",
                    ...(isMobile
                      ? { width: 20, height: 20, left: "-30px" }
                      : { width: 13, height: 13, left: "-20px" }),
                    color: singleEdge ? colors.palegreen : colors.red,
                  }}
                  isConnectable={isConnectable}
                />
                <div
                  className="display__port"
                  style={{
                    position: "absolute",
                    left: "-27px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: isConnectDemo
                      ? "!bg-[#316ACD]"
                      : "#c4c4c4",
                  }}
                ></div>
                <div className="ml-0 whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis capitalize text-[0.7rem] text-muted-foreground font-medium">
                  {connectedNodeType == BlocksType.DATA
                    ? connectedNodeName
                    : (name ?? "Input")}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right column: Output Ports */}
        <div className="flex flex-col items-end min-w-0">
          {data?.outputs?.map((port: any, index: number) => {
            const ids = port?.id;

            const outputEdge = edges?.find((item) => item?.source === id);
            const portName = port?.name;
            return (
              <div
                className="custom__port relative flex items-center h-10 px-0 pointer-events-auto"
                key={`output-${index}`}
              >
                <div
                  className={cn(
                    "mr-0 whitespace-nowrap text-right text-[0.7rem] text-muted-foreground font-medium",
                    data?.type === BlocksType.INDICATOR
                      ? "uppercase"
                      : "capitalize",
                  )}
                >
                  {portName}
                </div>
                <Popover open={isConnectDemo && data.name === "Price Data"}>
                  <PopoverTrigger asChild>
                    <Handle
                      type="source"
                      position={Position.Right}
                      className={cn(
                        "port-canvas nodrag",
                        isConnectDemo ? "!bg-[#316ACD]" : "",
                      )}
                      id={ids}
                      style={{
                        top: "50%",
                        transform: "translateY(-50%)",
                        ...(isMobile
                          ? { width: 20, height: 20, right: "-30px" }
                          : { width: 13, height: 13, right: "-25px" }),
                        color: outputEdge ? colors.palegreen : colors.red,
                      }}
                      isConnectable={isConnectable}
                    />
                  </PopoverTrigger>
                  <PopoverContent
                    align="center"
                    side="bottom"
                    className="text-xs w-40"
                    asChild
                  >
                    <div className="-top-20">
                      {isMobile && (
                        <div className="flex justify-end w-full cursor-pointer">
                          <MdClose size={16} onClick={() => {}} />
                        </div>
                      )}
                      <PopoverArrow />
                      {(isMobile ? " Zoom in and use" : " Use") +
                        " the drag-and-drop connectors to connect the inputs and outputs of all blocks (The blue connectors)"}
                    </div>
                  </PopoverContent>
                </Popover>
                <div
                  className="display__port"
                  style={{
                    position: "absolute",
                    right: "-27px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor:
                      portName !== "Else" ? "#27FF97" : "#c4c4c4",
                  }}
                ></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Handles;
